import { Dispatch, AnyAction } from 'redux';
import { NavigateFunction } from 'react-router-dom';

import * as api from '../utils/api';

import { updateAuthenticated } from './auth';
import { notifyError, processError, notifySaveSuccess, notifySuccess } from '../utils/functions';
import { getTeam } from './teams';
import { ErrorCodes } from '../utils/constants';
import { setActiveDeselectedCourses, setActiveTeams } from './features/teamsSlice';

interface IUserCourseDetails {
  assignedCourses: ICourse[];
  course: ICourse;
  courseAssignment?: ICourseAssignment;
  team?: ITeam;
}
interface UsersState {
  list?: IUser[];
  current?: IUser;
  courseDetails?: IUserCourseDetails;
}

const actions = {
  CLEAR_USER: 'lms-ui/users/CLEAR_USER',
  CLEAR_USERS_LIST: 'lms-ui/users/CLEAR_USERS_LIST',
  UPDATE: 'lms-ui/users/UPDATE',
  GET_USERS: 'lms-ui/users/GET_USERS',
  GET_USER: 'lms-ui/users/GET_USER',
  GET_USER_COURSE_DETAILS: 'lms-ui/users/GET_USER_COURSE_DETAILS',
};

interface UsersAction {
  type:
    | typeof actions.GET_USERS
    | typeof actions.GET_USER
    | typeof actions.UPDATE
    | typeof actions.GET_USER_COURSE_DETAILS;
  payload: IUser | IUser[] | IUserCourseDetails;
}

type UsersActionTypes = UsersAction;

const initialState: UsersState = {
  list: undefined,
  current: undefined,
};

export const update = (payload: UsersState) => {
  return {
    type: actions.UPDATE,
    payload,
  };
};

export const clearUser = () => {
  return {
    type: actions.CLEAR_USER,
  };
};

export const clearUsersList = () => {
  return {
    type: actions.CLEAR_USERS_LIST,
  };
};

export const getUsers = () => async (dispatch: Dispatch) => {
  try {
    const response = await api.getUsers();
    const list = response.data;
    dispatch({ type: actions.GET_USERS, payload: list });
  } catch (e) {
    notifyError(e, dispatch);
  }
};

export const getUserWithToken =
  (userId: string, token: string, navigate: NavigateFunction) => async (dispatch: Dispatch) => {
    try {
      const response = await api.getUserWithToken(userId, token);
      const current = response.data as IUser;
      dispatch({ type: actions.GET_USER, payload: current });
    } catch (e) {
      const error = processError(e, dispatch);
      if (error.errorCode === ErrorCodes.InvalidToken) {
        navigate('/login?tokenError=1');
      }
    }
  };

export const getUser = (userId: string) => async (dispatch: Dispatch) => {
  try {
    const response = await api.getUser(userId);
    const current = response.data as IUser;
    dispatch({ type: actions.GET_USER, payload: current });
  } catch (e) {
    notifyError(e, dispatch);
  }
};

export const getCourseProgressDetails =
  (userId: string, courseId: string) => async (dispatch: Dispatch) => {
    try {
      const response = await api.getCourseProgressDetails(userId, courseId);
      const data = response.data as IUserCourseDetails;
      dispatch({ type: actions.GET_USER_COURSE_DETAILS, payload: data });
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const resetCourseProgress =
  ({ userId, courseId, all }: { userId: string; courseId?: string; all?: boolean }) =>
  async (dispatch: Dispatch) => {
    try {
      if (all) {
        await api.resetAllProgress(userId);
        dispatch(getUser(userId) as any);
      } else if (courseId) {
        await api.resetCourseProgress(userId, courseId);
        dispatch(getCourseProgressDetails(userId, courseId) as any);
      }
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const bulkUploadUsers = (users: IUser[]) => async (dispatch: Dispatch) => {
  let data = [];
  try {
    const response = await api.bulkUploadUsers(users);
    notifySaveSuccess();
    data = response.data.map((d: any) => ({ ...d, hasError: true }));
  } catch (e) {
    notifyError(e, dispatch);
  }

  return data;
};

export const bulkEditUsers = (users: IUser[]) => async (dispatch: Dispatch) => {
  try {
    await api.bulkEditUsers(users);
    notifySaveSuccess();
  } catch (e) {
    notifyError(e, dispatch);
  }
};

export const signupUser =
  (user: IUser, navigate: NavigateFunction) => async (dispatch: Dispatch) => {
    try {
      const response = await api.signupUser(user);
      const updatedUser = response.data as IUser;
      dispatch(updateAuthenticated(updatedUser));

      if (updatedUser.forceRegistration) {
        navigate(`/settings?signup=1`);
      } else {
        navigate('/');
      }

      notifySuccess(`Code redeemed: ${user.code}`);
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const signupAuthedUser =
  (code: string, navigate: NavigateFunction) => async (dispatch: Dispatch) => {
    try {
      const response = await api.signupAuthedUser(code);
      const updatedUser = response.data as IUser;
      dispatch(updateAuthenticated(updatedUser));

      if (updatedUser.forceRegistration) {
        navigate(`/settings?signup=1`);
      } else {
        navigate('/');
      }

      notifySuccess(`Code redeemed: ${code}`);
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const registerUser =
  (user: IUser, token: string, navigate: NavigateFunction) => async (dispatch: Dispatch) => {
    try {
      const response = await api.registerUser(user, token);
      dispatch(updateAuthenticated(response.data));
      navigate(`/`);
      notifySuccess('Registration successful!');
    } catch (e) {
      const finalError = processError(e, dispatch);
      return Promise.reject(finalError);
    }
  };

export const forgotPassword =
  (data: { email: string; token: string }) => async (dispatch: Dispatch) => {
    try {
      await api.forgotPassword(data);
    } catch (e) {
      const finalError = notifyError(e, dispatch);
      return Promise.reject(finalError);
    }
  };

export const resetPassword =
  (
    data: {
      userId: string;
      password: string;
      resetToken: string;
      token: string;
    },
    navigate: NavigateFunction,
  ) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await api.resetPassword(data);
      dispatch(updateAuthenticated(response.data));
      navigate(`/`);
      notifySuccess('Your password has been successfully reset!');
    } catch (e) {
      const finalError = processError(e, dispatch);
      return Promise.reject(finalError);
    }
  };

const getFormData = (user: IUser) => {
  const formData = new FormData();
  const props = [
    '_id',
    'email',
    'firstName',
    'lastName',
    'password',
    'position',
    'roles',
    'cles',
    'optOutCLEs',
    'practiceGroup',
    'firmOffice',
    'courses',
    'courseAssignments',
    'unassignedCourses',
    'currentPassword',
    'reportNotification',
    'reportDeselectedCourses',
    'reportTeams',
  ];

  props.forEach((p) => {
    const value = (user as IKey)[p];
    if (value !== undefined && value !== null) {
      switch (p) {
        case 'cles':
          value?.forEach((v: ICLE, i: number) => {
            Object.keys(v).forEach((k) => {
              formData.append(`${p}[${i}][${k}]`, v[k]);
            });
          });
          break;
        case 'roles':
          value?.forEach((v: IRole, i: number) => {
            Object.keys(v).forEach((k) => {
              formData.append(`${p}[${i}][${k}]`, k === 'includeSubteams' ? !!v[k] : v[k]);
            });
          });
          break;
        case 'courses':
          value
            ?.filter((c: any) => !c.teamId)
            .forEach((c: any) => {
              formData.append(`${p}[]`, typeof c === 'string' ? c : c._id);
            });
          break;
        case 'unassignedCourses':
          value?.forEach((v: IUnassignedCourse, i: number) => {
            Object.keys(v).forEach((k) => {
              formData.append(`${p}[${i}][${k}]`, v[k]);
            });
          });
          break;
        case 'courseAssignments':
          value?.forEach((v: ICourseAssignment, i: number) => {
            (Object.keys(v) as Array<keyof ICourseAssignment>).forEach((k) => {
              if (v[k]) {
                formData.append(`${p}[${i}][${k}]`, v[k] as any);
              }
            });
          });
          break;
        case 'reportNotification':
          Object.keys(value).forEach((k) => {
            let data = value[k];
            if (data || data === 0) {
              if (k === 'day') {
                const date = new Date();
                const current = date.getDay();
                const difference = data - current;
                date.setDate(date.getDate() + difference);
                data = date.getUTCDay();
              } else if (k === 'date') {
                const date = new Date(`01/${data}/2021`);
                data = date.getUTCDate();
              }
            }
            formData.append(`${p}[${k}]`, data);
          });
          break;
        case 'reportDeselectedCourses':
          value
            ?.filter((id: any) => id)
            .forEach((id: any) => {
              formData.append(`${p}[]`, id);
            });
          break;
        case 'reportTeams':
          value
            ?.filter((id: any) => id)
            .forEach((id: any) => {
              formData.append(`${p}[]`, id);
            });
          break;
        case 'email':
          formData.append(p, value.replaceAll('’', `'`));
          break;
        default:
          formData.append(p, value.toString());
          break;
      }
    }
  });

  return formData;
};

export const addUser = (user: IUser) => async (dispatch: Dispatch) => {
  try {
    const formData = getFormData(user);
    const response = await api.addUser(formData);
    const current = response.data as IUser;
    dispatch(update({ current }));
    notifySuccess('User added');
    return Promise.resolve(current);
  } catch (e) {
    const finalError = notifyError(e, dispatch);
    return Promise.reject(finalError);
  }
};

export const updateUser =
  (user: IUser, isSettings?: boolean, navigate?: NavigateFunction) =>
  async (dispatch: Dispatch) => {
    try {
      const formData = getFormData(user);
      let response;
      if (isSettings) {
        response = await api.updateSelf(formData);
      } else {
        response = await api.updateUser(user._id as string, formData);
      }
      const current = response.data as IUser;

      dispatch(update({ current }));
      dispatch(setActiveTeams(current.reportTeams || []));
      dispatch(setActiveDeselectedCourses(current.reportDeselectedCourses || []));

      if (isSettings) {
        dispatch(updateAuthenticated(current));
        if (
          (user.forcePasswordReset || user.forceRegistration || user.unmetRequirements) &&
          navigate
        ) {
          navigate(`/`);
          if (user.forcePasswordReset) {
            notifySuccess('Your password has been successfully reset!');
          }
        }
      }

      return Promise.resolve();
    } catch (e) {
      // Only process the error if isSettings is true so non-admins don't
      // get a toast message
      const finalError = isSettings ? processError(e, dispatch) : notifyError(e, dispatch);
      return Promise.reject(finalError);
    }
  };

export const deleteUser =
  (userId: string, navigate: NavigateFunction, navUrl?: string, teamId?: string) =>
  async (dispatch: Dispatch) => {
    try {
      await api.deleteUser(userId);
      navigate(navUrl || `/admin/users`);
      notifySuccess('User deleted');

      if (teamId) {
        // Had to do this weird cast to get TS to not complain
        dispatch(getTeam(teamId) as unknown as AnyAction);
      }

      // TODO: At some point we need to implement Redux best-practices
      // for typing so that we don't need to do this
      // See: https://stackoverflow.com/a/72399130
      // https://redux.js.org/tutorials/typescript-quick-start
      // https://redux.js.org/tutorials/index
      dispatch(getUsers() as unknown as AnyAction);
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const expirePassword = (userId: string) => async (dispatch: Dispatch) => {
  try {
    const response = await api.expirePassword(userId);
    const current = response.data as IUser;
    dispatch(update({ current }));
    notifySuccess('Password expired');
  } catch (e) {
    notifyError(e, dispatch);
  }
};

export const sendUserWelcomeEmail = (userId: string) => async (dispatch: Dispatch) => {
  try {
    await api.sendUserWelcomeEmail(userId);
    notifySuccess('Email sent!');
  } catch (e) {
    notifyError(e, dispatch);
  }
};

export const updateCourseProgressDetails =
  ({ userId, courseId, details }: { userId: string; courseId: string; details: any }) =>
  async (dispatch: Dispatch) => {
    try {
      let response = await api.updateCourseProgressDetails({
        userId,
        courseId,
        details,
      });

      const current = response.data as IUser;

      dispatch(getCourseProgressDetails(userId, courseId) as any);
      dispatch(update({ current }));
      notifySuccess('Assignment details updated');
      return Promise.resolve(true);
    } catch (e) {
      notifyError(e, dispatch);
    }

    return Promise.resolve(false);
  };

export default function reducer(state = initialState, action: UsersActionTypes): UsersState {
  switch (action.type) {
    case actions.UPDATE: {
      return {
        ...state,
        ...action.payload,
      };
    }
    case actions.GET_USERS: {
      return {
        ...state,
        list: action.payload as IUser[],
      };
    }
    case actions.GET_USER: {
      return {
        ...state,
        current: action.payload as IUser,
      };
    }
    case actions.GET_USER_COURSE_DETAILS: {
      return {
        ...state,
        courseDetails: action.payload as IUserCourseDetails,
      };
    }
    case actions.CLEAR_USER: {
      return {
        ...state,
        current: undefined,
      };
    }
    case actions.CLEAR_USERS_LIST: {
      return {
        ...state,
        list: undefined,
      };
    }
    default:
      return state;
  }
}
